import React, { useEffect, useRef } from 'react';
import { useAccessibility } from './AccessibilityContext';
import { radius, components } from '../config/designTokens';

const AccessibilityPanel = ({ isOpen, onClose }) => {
  const {
    isAccessibilityEnabled,
    accessibilityFeatures,
    textScale,
    toggleAccessibility,
    updateAccessibilityFeature,
    updateTextScale,
    resetAccessibilityFeatures
  } = useAccessibility();

  const panelRef = useRef(null);

  // Close panel on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      
      // Focus the panel for accessibility
      if (panelRef.current) {
        panelRef.current.focus();
      }
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  const accessibilityOptions = [
    {
      id: 'highContrast',
      label: 'ניגודיות גבוהה',
      icon: '🎨',
      type: 'toggle'
    },
    {
      id: 'largeText',
      label: 'טקסט מוגדל',
      icon: '🔍',
      type: 'size-control'
    },
    {
      id: 'reducedMotion',
      label: 'צמצום אנימציות',
      icon: '⏸️',
      type: 'toggle'
    },
    {
      id: 'screenReaderOptimized',
      label: 'אופטימיזציה לקורא מסך',
      icon: '🔊',
      type: 'toggle'
    }
  ];

  // Expert toggle function with proper announcement
  const handleFeatureToggle = (featureId) => {
    const newValue = !accessibilityFeatures[featureId];
    updateAccessibilityFeature(featureId, newValue);
  };

  const handleTextSizeChange = (increment) => {
    const newScale = textScale + increment;
    updateTextScale(newScale);
  };

  const handleMainToggle = () => {
    toggleAccessibility();
  };

  // Expert toggle switch component
  const ToggleSwitch = ({ checked, onChange, label, disabled = false }) => {
    return (
      <button
        onClick={onChange}
        className="accessibility-toggle-switch"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        disabled={disabled}
        type="button"
      />
    );
  };

  if (!isOpen) return null;

  // Apply reduced motion styles if needed
  const animationClass = accessibilityFeatures.reducedMotion 
    ? 'animate-fade-in' 
    : 'animate-slide-in';

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-40 accessibility-modal-overlay z-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="accessibility-panel-title"
      onClick={onClose}
      dir="rtl"
    >
      <div 
        ref={panelRef}
        className={`accessibility-panel ${animationClass}`}
        onClick={e => e.stopPropagation()}
        tabIndex="-1"
      >
        {/* Header */}
        <div className={`sticky top-0 bg-white ${radius.xxl} border-b border-gray-200 p-4 z-10 flex-shrink-0`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 ${radius.lg} flex items-center justify-center flex-shrink-0`}>
                <span className="text-lg" role="img" aria-label="נגישות">♿</span>
              </div>
              <div className="min-w-0 flex-1">
                <h2 id="accessibility-panel-title" className="text-lg font-bold text-gray-900 truncate">
                  הגדרות נגישות
                </h2>
                <p className="text-xs text-gray-600">
                  <span className="inline-flex items-center gap-1">
                    <span className={`w-1.5 h-1.5 bg-green-500 ${radius.full} flex-shrink-0`}></span>
                    <span>WCAG 2.1 AA</span>
                  </span>
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className={`w-8 h-8 flex items-center justify-center ${radius.lg} hover:bg-gray-100 transition-colors duration-200 ${components.focus.secondary} flex-shrink-0`}
              aria-label="סגור פאנל נגישות"
            >
              <span className="text-lg text-gray-600">✕</span>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-4 space-y-4">
            {/* Main Toggle */}
            <div className={`bg-gradient-to-r from-blue-50 to-blue-100 p-4 ${radius.xl} border border-blue-200`}>
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <span className="text-lg flex-shrink-0" role="img" aria-label="הפעלה כללית">🔧</span>
                  <span className="font-semibold text-gray-900 truncate">נגישות כללית</span>
                </div>
                <ToggleSwitch 
                  checked={isAccessibilityEnabled}
                  onChange={handleMainToggle}
                  label="נגישות כללית"
                />
              </div>
            </div>

            {/* Features */}
            {isAccessibilityEnabled && (
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                  <span role="img" aria-label="הגדרות" className="flex-shrink-0">⚙️</span>
                  <span>הגדרות מתקדמות</span>
                </h3>
                
                <div className="space-y-3">
                  {accessibilityOptions.map((option) => {
                    const isEnabled = accessibilityFeatures[option.id];
                    
                    return (
                      <div 
                        key={option.id} 
                        className={`p-3 border ${radius.xl} transition-all duration-200 ${
                          isEnabled 
                            ? 'border-blue-200 bg-blue-50' 
                            : 'border-gray-200 bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-3 min-w-0 flex-1">
                            <span 
                              className={`text-lg flex-shrink-0 ${
                                isEnabled ? 'text-blue-700' : 'text-gray-600'
                              }`} 
                              role="img" 
                              aria-label={option.label}
                            >
                              {option.icon}
                            </span>
                            <span className="font-medium text-gray-900 text-sm truncate">
                              {option.label}
                            </span>
                          </div>
                          
                          {option.type === 'toggle' ? (
                            <ToggleSwitch 
                              checked={isEnabled}
                              onChange={() => handleFeatureToggle(option.id)}
                              label={option.label}
                            />
                          ) : (
                            // Text size controls
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <button
                                onClick={() => handleTextSizeChange(-25)}
                                className={`w-8 h-8 flex items-center justify-center bg-gray-200 hover:bg-gray-300 ${radius.full} text-gray-700 font-bold ${components.focus.secondary} transition-colors duration-200 flex-shrink-0`}
                                aria-label="הקטן טקסט"
                                disabled={textScale <= 75}
                              >
                                −
                              </button>
                              <span className="text-sm font-medium min-w-[3rem] text-center flex-shrink-0">
                                {textScale}%
                              </span>
                              <button
                                onClick={() => handleTextSizeChange(25)}
                                className={`w-8 h-8 flex items-center justify-center bg-gray-200 hover:bg-gray-300 ${radius.full} text-gray-700 font-bold ${components.focus.secondary} transition-colors duration-200 flex-shrink-0`}
                                aria-label="הגדל טקסט"
                                disabled={textScale >= 200}
                              >
                                +
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 pt-2">
              <button
                onClick={resetAccessibilityFeatures}
                className={`flex-1 px-4 py-2 text-gray-700 bg-gray-100 ${radius.lg} hover:bg-gray-200 transition-colors duration-200 ${components.focus.secondary} font-medium text-sm min-h-[2.5rem]`}
                disabled={!isAccessibilityEnabled}
              >
                🔄 איפוס
              </button>
              <button
                onClick={onClose}
                className={`flex-1 px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white ${radius.lg} hover:from-blue-600 hover:to-blue-600 transition-all duration-200 ${components.focus.secondary} font-medium text-sm min-h-[2.5rem]`}
              >
                ✓ סגור
              </button>
            </div>

            {/* Info */}
            <div className={`bg-gradient-to-r from-teal-50 to-blue-50 p-3 ${radius.xl} border border-teal-200`}>
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <span role="img" aria-label="תקן נגישות" className="text-lg flex-shrink-0">🏆</span>
                  <span className="text-sm font-bold text-gray-900">WCAG 2.1 AA</span>
                </div>
                <p className="text-xs text-gray-700">
                  <strong>Nice Ice</strong> - נגישות דיגיטלית לכולם
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccessibilityPanel;